
/**
 * Drawing primitives for Alloy's state-illustration family (empty, error,
 * access and offline states). Every scene shares one 240×160 canvas, one soft
 * drop shadow, hairline strokes and a single small accent, so a scene built
 * from these parts cannot drift from the rest of the family. All colour comes
 * from the `--ads-illo-*` tokens in app/globals.css.
 */

import * as React from "react";
import type { LucideIcon } from "lucide-react";

export type IlloAccent = "green" | "blue" | "purple" | "red" | "amber";

interface AccentPalette {
  solid: string;
  soft: string;
  line: string;
}

const ACCENTS: Record<IlloAccent, AccentPalette> = {
  green: { solid: "var(--ads-illo-green)", soft: "var(--ads-illo-green-soft)", line: "var(--ads-illo-green-line)" },
  blue: { solid: "var(--ads-illo-blue)", soft: "var(--ads-illo-blue-soft)", line: "var(--ads-illo-blue-line)" },
  purple: { solid: "var(--ads-illo-purple)", soft: "var(--ads-illo-purple-soft)", line: "var(--ads-illo-purple-line)" },
  red: { solid: "var(--ads-illo-red)", soft: "var(--ads-illo-red-soft)", line: "var(--ads-illo-red-line)" },
  amber: { solid: "var(--ads-illo-amber)", soft: "var(--ads-illo-amber-soft)", line: "var(--ads-illo-amber-line)" },
};

const C = {
  surface: "var(--ads-illo-surface)",
  stroke: "var(--ads-illo-stroke)",
  bar: "var(--ads-illo-bar)",
  barSoft: "var(--ads-illo-bar-soft)",
  dot: "var(--ads-illo-dot)",
  glyph: "var(--ads-illo-glyph)",
  handle: "var(--ads-illo-handle)",
  ringDark: "var(--ads-illo-ring-dark)",
  lens: "var(--ads-illo-lens)",
} as const;

interface SceneContext {
  accent: AccentPalette;
  shadow: string;
  shadowSm: string;
  ring: string;
}

const Ctx = React.createContext<SceneContext | null>(null);

function useScene(): SceneContext {
  const ctx = React.useContext(Ctx);
  if (!ctx) throw new Error("Illustration primitives must render inside <Scene>.");
  return ctx;
}

export interface SceneProps {
  className?: string;
  /** Accessible name. Omit for decorative use (the default next to a visible heading). */
  title?: string;
}

export function Scene({
  accent,
  className,
  title,
  children,
}: SceneProps & { accent: IlloAccent; children: React.ReactNode }) {
  const uid = React.useId().replace(/[^a-zA-Z0-9]/g, "");
  const ids = { shadow: `illo${uid}s`, shadowSm: `illo${uid}t`, ring: `illo${uid}r`, glow: `illo${uid}g` };
  const palette = ACCENTS[accent];

  return (
    <svg
      viewBox="0 0 240 160"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      <defs>
        <filter id={ids.shadow} x="-25%" y="-25%" width="150%" height="170%" colorInterpolationFilters="sRGB">
          <feDropShadow dx="0" dy="6" stdDeviation="6" style={{ floodColor: "var(--ads-illo-shadow)", floodOpacity: 0.09 }} />
        </filter>
        <filter id={ids.shadowSm} x="-40%" y="-40%" width="180%" height="190%" colorInterpolationFilters="sRGB">
          <feDropShadow dx="0" dy="2" stdDeviation="2.4" style={{ floodColor: "var(--ads-illo-shadow)", floodOpacity: 0.14 }} />
        </filter>
        <linearGradient id={ids.ring} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" style={{ stopColor: "var(--ads-illo-ring-light)" }} />
          <stop offset="1" style={{ stopColor: "var(--ads-illo-ring-dark)" }} />
        </linearGradient>
        <radialGradient id={ids.glow} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" style={{ stopColor: palette.soft, stopOpacity: 0.85 }} />
          <stop offset="1" style={{ stopColor: palette.soft, stopOpacity: 0 }} />
        </radialGradient>
      </defs>
      {/* Soft ambient glow in the scene accent, behind everything. */}
      <ellipse cx="120" cy="84" rx="108" ry="70" fill={`url(#${ids.glow})`} />
      <Ctx.Provider value={{ accent: palette, shadow: ids.shadow, shadowSm: ids.shadowSm, ring: ids.ring }}>
        {children}
      </Ctx.Provider>
    </svg>
  );
}

function rotateAbout(rotate: number, cx: number, cy: number) {
  return rotate ? `rotate(${rotate} ${cx} ${cy})` : undefined;
}

/** A floating UI card. Children are drawn in the card's local coordinates. */
export function Card({
  x,
  y,
  w,
  h,
  r = 7,
  rotate = 0,
  tone = "plain",
  children,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  r?: number;
  rotate?: number;
  /** plain: hairline card · accent: accent outline (the scene's focal card) · dashed: placeholder slot. */
  tone?: "plain" | "accent" | "dashed";
  children?: React.ReactNode;
}) {
  const { accent, shadow } = useScene();
  const dashed = tone === "dashed";
  return (
    <g transform={rotateAbout(rotate, x + w / 2, y + h / 2)}>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={r}
        fill={C.surface}
        fillOpacity={dashed ? 0.7 : 1}
        stroke={tone === "accent" ? accent.line : dashed ? C.bar : C.stroke}
        strokeWidth={tone === "accent" ? 1.25 : 1}
        strokeDasharray={dashed ? "4 3" : undefined}
        filter={dashed ? undefined : `url(#${shadow})`}
      />
      {children && <g transform={`translate(${x} ${y})`}>{children}</g>}
    </g>
  );
}

/** A document page with a folded corner. Children are drawn in local coordinates. */
export function DocPage({
  x,
  y,
  w,
  h,
  rotate = 0,
  tone = "plain",
  children,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  rotate?: number;
  tone?: "plain" | "accent";
  children?: React.ReactNode;
}) {
  const { accent, shadow } = useScene();
  const f = 14;
  const r = 7;
  const outline = `M${x + r} ${y} H${x + w - f} L${x + w} ${y + f} V${y + h - r} Q${x + w} ${y + h} ${x + w - r} ${y + h} H${x + r} Q${x} ${y + h} ${x} ${y + h - r} V${y + r} Q${x} ${y} ${x + r} ${y} Z`;
  const fold = `M${x + w - f} ${y} V${y + f - 4} Q${x + w - f} ${y + f} ${x + w - f + 4} ${y + f} H${x + w}`;
  const stroke = tone === "accent" ? accent.line : C.stroke;
  return (
    <g transform={rotateAbout(rotate, x + w / 2, y + h / 2)}>
      <path d={outline} fill={C.surface} stroke={stroke} strokeWidth={tone === "accent" ? 1.25 : 1} filter={`url(#${shadow})`} />
      <path d={fold} fill={C.barSoft} stroke={stroke} strokeWidth={1} strokeLinejoin="round" />
      {children && <g transform={`translate(${x} ${y})`}>{children}</g>}
    </g>
  );
}

/** Rounded icon tile — solid accent (focal) or soft accent (supporting). */
export function Tile({
  x,
  y,
  s = 18,
  icon: Icon,
  solid = false,
  muted = false,
}: {
  x: number;
  y: number;
  s?: number;
  icon: LucideIcon;
  solid?: boolean;
  /** Neutral grey tile, for placeholder and unavailable content. */
  muted?: boolean;
}) {
  const { accent } = useScene();
  const pad = s * 0.22;
  return (
    <g>
      <rect x={x} y={y} width={s} height={s} rx={s * 0.28} fill={muted ? C.barSoft : solid ? accent.solid : accent.soft} />
      <Icon
        x={x + pad}
        y={y + pad}
        width={s - pad * 2}
        height={s - pad * 2}
        strokeWidth={2.1}
        color={muted ? C.dot : solid ? C.surface : C.glyph}
      />
    </g>
  );
}

/** Skeleton text: a heavier title bar followed by lighter detail bars. */
export function Lines({ x, y, widths, gap = 8 }: { x: number; y: number; widths: number[]; gap?: number }) {
  return (
    <g>
      {widths.map((w, i) => (
        <rect
          key={i}
          x={x}
          y={i === 0 ? y : y + 11 + (i - 1) * gap}
          width={w}
          height={i === 0 ? 5 : 3.5}
          rx={i === 0 ? 2.5 : 1.75}
          fill={i === 0 ? C.bar : C.barSoft}
        />
      ))}
    </g>
  );
}

export function Bar({ x, y, w, h = 3.5, soft = true }: { x: number; y: number; w: number; h?: number; soft?: boolean }) {
  return <rect x={x} y={y} width={w} height={h} rx={h / 2} fill={soft ? C.barSoft : C.bar} />;
}

/** The three window dots on a card header — grey, or pastel on the focal card. */
export function Dots({ x, y, pastel = false }: { x: number; y: number; pastel?: boolean }) {
  const fills = pastel
    ? ["var(--ads-illo-pastel-1)", "var(--ads-illo-pastel-2)", "var(--ads-illo-pastel-3)"]
    : [C.dot, C.dot, C.dot];
  return (
    <g>
      {fills.map((f, i) => (
        <circle key={i} cx={x + i * 6} cy={y} r={2.2} fill={f} />
      ))}
    </g>
  );
}

/** Graphite magnifier with a looped handle; `angle` points the handle (135° = lower-left). */
export function Magnifier({ cx, cy, r = 24, angle = 135 }: { cx: number; cy: number; r?: number; angle?: number }) {
  const { ring, shadow } = useScene();
  const sw = r * 0.24;
  return (
    <g filter={`url(#${shadow})`}>
      <circle cx={cx} cy={cy} r={r} fill={C.lens} fillOpacity={0.4} />
      <g transform={`translate(${cx} ${cy}) rotate(${angle})`}>
        <rect x={r + sw / 2 - 1} y={-sw * 0.3} width={r * 0.3} height={sw * 0.6} fill={C.ringDark} />
        <rect x={r * 1.24} y={-r * 0.3} width={r * 1.02} height={r * 0.6} rx={r * 0.3} fill={C.handle} />
        <rect x={r * 1.46} y={-r * 0.085} width={r * 0.58} height={r * 0.17} rx={r * 0.085} fill={C.surface} />
      </g>
      <circle cx={cx} cy={cy} r={r} stroke={`url(#${ring})`} strokeWidth={sw} />
      <path
        d={`M${cx - r * 0.62} ${cy - r * 0.2} A${r * 0.66} ${r * 0.66} 0 0 1 ${cx - r * 0.18} ${cy - r * 0.62}`}
        stroke={C.surface}
        strokeOpacity={0.9}
        strokeWidth={1.6}
        strokeLinecap="round"
      />
    </g>
  );
}

/** Small circular status indicator with a white ring. Tone defaults to the scene accent. */
export function Badge({
  cx,
  cy,
  r = 10,
  icon: Icon,
  tone,
}: {
  cx: number;
  cy: number;
  r?: number;
  icon: LucideIcon;
  tone?: IlloAccent;
}) {
  const { accent, shadowSm } = useScene();
  const fill = tone ? ACCENTS[tone].solid : accent.solid;
  const g = r * 1.1;
  return (
    <g filter={`url(#${shadowSm})`}>
      <circle cx={cx} cy={cy} r={r} fill={fill} stroke={C.surface} strokeWidth={2.5} />
      <Icon x={cx - g / 2} y={cy - g / 2} width={g} height={g} strokeWidth={3} color={C.surface} />
    </g>
  );
}

/** Graphite padlock — same material as the magnifier so access scenes stay in family. */
export function Padlock({ cx, cy, s = 44 }: { cx: number; cy: number; s?: number }) {
  const { ring, shadow } = useScene();
  const bodyY = cy - s * 0.1;
  const sr = s * 0.27;
  return (
    <g filter={`url(#${shadow})`}>
      <path
        d={`M${cx - sr} ${bodyY + 2} V${bodyY - sr * 0.55} A${sr} ${sr} 0 0 1 ${cx + sr} ${bodyY - sr * 0.55} V${bodyY + 2}`}
        stroke={`url(#${ring})`}
        strokeWidth={s * 0.12}
        strokeLinecap="round"
      />
      <rect x={cx - s / 2} y={bodyY} width={s} height={s * 0.78} rx={s * 0.18} fill={C.handle} />
      <circle cx={cx} cy={bodyY + s * 0.34} r={s * 0.08} fill={C.surface} />
      <rect x={cx - s * 0.03} y={bodyY + s * 0.36} width={s * 0.06} height={s * 0.17} rx={s * 0.03} fill={C.surface} />
    </g>
  );
}

/** Soft cloud silhouette (connectivity scenes). */
export function Cloud({ cx, cy, scale = 1 }: { cx: number; cy: number; scale?: number }) {
  const { shadow } = useScene();
  return (
    <g transform={`translate(${cx} ${cy}) scale(${scale})`} filter={`url(#${shadow})`}>
      <g fill={C.surface}>
        <circle cx={-20} cy={2} r={16} />
        <circle cx={4} cy={-8} r={22} />
        <circle cx={26} cy={4} r={14} />
        <rect x={-36} y={2} width={76} height={16} rx={8} />
      </g>
    </g>
  );
}

/** Dashed connector between two points; `broken` leaves a gap in the middle for a badge. */
export function Connector({
  x1,
  y1,
  x2,
  y2,
  broken = false,
  solid = false,
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  broken?: boolean;
  solid?: boolean;
}) {
  const { accent } = useScene();
  const props = {
    stroke: broken ? C.bar : accent.line,
    strokeWidth: 1.5,
    strokeDasharray: solid ? undefined : "4 3",
    strokeLinecap: "round" as const,
  };
  if (!broken) return <line x1={x1} y1={y1} x2={x2} y2={y2} {...props} />;
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const gx = (x2 - x1) * 0.18;
  const gy = (y2 - y1) * 0.18;
  return (
    <g>
      <line x1={x1} y1={y1} x2={mx - gx} y2={my - gy} {...props} />
      <line x1={mx + gx} y1={my + gy} x2={x2} y2={y2} {...props} />
    </g>
  );
}

/** Accent sparkline / pulse inside a card (local coordinates). */
export function Sparkline({ points, faded = false }: { points: [number, number][]; faded?: boolean }) {
  const { accent } = useScene();
  return (
    <polyline
      points={points.map((p) => p.join(",")).join(" ")}
      stroke={faded ? C.bar : accent.solid}
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  );
}

/** Row of mini columns inside a card; `empty` draws them as unfilled outlines. */
export function Columns({
  x,
  baseline,
  heights,
  w = 9,
  step = 16,
  empty = false,
  highlight,
}: {
  x: number;
  baseline: number;
  heights: number[];
  w?: number;
  step?: number;
  empty?: boolean;
  /** Index of a column drawn in the accent. */
  highlight?: number;
}) {
  const { accent } = useScene();
  return (
    <g>
      {heights.map((h, i) => (
        <rect
          key={i}
          x={x + i * step}
          y={baseline - h}
          width={w}
          height={h}
          rx={2.5}
          fill={empty ? "none" : i === highlight ? accent.solid : C.barSoft}
          stroke={empty ? C.bar : undefined}
          strokeDasharray={empty ? "3 2.5" : undefined}
        />
      ))}
      <line x1={x - 4} y1={baseline + 0.5} x2={x + (heights.length - 1) * step + w + 4} y2={baseline + 0.5} stroke={C.stroke} />
    </g>
  );
}

/** Small accent-filled circle, e.g. a status dot or avatar bubble. */
export function Dot({ cx, cy, r = 3, tone, soft = false }: { cx: number; cy: number; r?: number; tone?: IlloAccent; soft?: boolean }) {
  const { accent } = useScene();
  const palette = tone ? ACCENTS[tone] : accent;
  return <circle cx={cx} cy={cy} r={r} fill={soft ? palette.soft : palette.solid} />;
}

/** Folder with papers tucked inside. */
export function Folder({ x, y, w, h, children }: { x: number; y: number; w: number; h: number; children?: React.ReactNode }) {
  const { accent, shadow } = useScene();
  return (
    <g>
      <rect x={x} y={y} width={w * 0.4} height={18} rx={6} fill={accent.soft} stroke={accent.line} />
      <rect x={x} y={y + 8} width={w} height={h - 8} rx={8} fill={accent.soft} stroke={accent.line} />
      {children}
      <rect x={x} y={y + h * 0.34} width={w} height={h * 0.66} rx={8} fill={C.surface} stroke={C.stroke} filter={`url(#${shadow})`} />
    </g>
  );
}

export const illoColors = C;
